<template>
    <div class="search-results">
        <!-- TODO: Waiting for client feedback on removing this total results info -->
        <!-- <div class="search-results__count" v-show="(resultsCount > 0 || query.length > 2) && ! state.loading">{{ resultsCount }} total results for "{{ query }}"</div> -->
        <div class="search-results__tabs-wrap" v-show="resultsCount > 0">
            <ul
                class="search-results__tabs"
                role="tablist"
            >
                <li
                    v-for="(result, key) in results"
                    :key="key"
                    :class="['search-results__tab-label', { '-is-active': result.active, '-no-results': result.data.length == 0 }]"
                    @click="toggleTab(result)"
                    @keyup.enter="toggleTab(result)"
                    :aria-label="`${key} tab with ${result.data.length} results.`"
                    role="tab"
                    :aria-selected="result.active ? 'true' : 'false'"
                    :id="`searchResultTab-${key}`"
                    :aria-disabled="result.data.length > 0 ? 'false' : 'true'"
                    tabindex="0"
                    :ref="result.active ? 'activeTab' : 'inactiveTab'"
                >
                    <span class="search-results__item-name" aria-live="assertive" aria-atomic="true">
                        {{ result.name }}
                    </span>
                    <span v-if="key === 'top_results'" class="search-results__item-count">
                        {{ suggestedTopResults.length }}
                    </span>
                    <span class="search-results__item-count" v-else>
                        {{ result.data.length }}
                    </span>
                </li>
            </ul>
        </div>
        <div class="search-results__tab-content" :id="`searchResults-${id}`" ref="searchResults">
            <div
                class="search-results__tab"
                v-for="(resultType, key) in results"
                :key="key"
                v-show="resultType.active"
                role="tabpanel"
                :aria-hidden="resultType.active ? 'false' : 'true'"
                :aria-labelledby="`searchResultTab-${key}`"
            >
                <div :class="['search-results__results', `-${getComponent(key)}`]">
                    <template v-if="key === 'top_results'">
                        <component
                            :is="getComponent(key)"
                            :active="resultType.active"
                            :allResults="filteredTopResults"
                            :name="resultType.name"
                            tabindex="0"
                        />
                    </template>
                    <template v-else>
                        <component
                            :is="getComponent(key)"
                            :data="item"
                            v-for="(item, cardKey) in resultType.data"
                            :key="cardKey"
                            :request-id="requestId"
                            :query="query"
                            tabindex="0"
                        />
                    </template>
                </div>
            </div>
        </div>
        <div class="sr-only" aria-live="assertive" aria-atomic="true">{{ resultsCount }} total results for "{{ query }}"</div>
        <div class="sr-only" aria-live="polite">
            Tab changed.  Selected tab is {{ state.currentTab.name }}.  There are {{ state.currentTab.data ? state.currentTab.data.length : 0  }} results under this tab.
        </div>
    </div>
</template>

<script>
import { $extend, $isFunction } from 'core/types';
import $mediator from 'wee-mediator';
import Tabbable from '../../../scripts/tabbable.js';
import personnel from '../types/personnel.vue';
import location from '../types/location.vue';
import service from '../types/service.vue';
import specialty from '../types/specialty.vue';
import other from '../types/other.vue';
import clinicalTrial from '../types/clinical-trial.vue';
import transforms from '../transforms.js';
import topResults from './top-results.vue';

const baseResults = {
    top_results: {
        name: 'Top Results',
        active: false,
        data: [],
    },
    personnel: {
        name: 'Providers',
        active: false,
        data: []
    },
    location: {
        name: 'Locations',
        active: false,
        data: [],
    },
    specialty: {
        name: 'Specialties',
        active: false,
        data: [],
    },
    service: {
        name: 'Services',
        active: false,
        data: [],
    },
    clinical_trial: {
        name: 'Clinical Trials',
        active: false,
        data: [],
    },
    other: {
        name: 'Other',
        active: false,
        data: [],
    },
};

let tabbable = null;
let tabbableTimeout = null;

export default {
    props: {
        id: {
            type: Number,
        },
        items: {
            type: Array,
        },
        query: {
            type: String,
        },
        requestId: {
            type: String,
        },
        suggestedTopResults: {
            type: Array,
        },
    },
    components: {
        personnel,
        location,
        service,
        specialty,
        other,
        clinicalTrial,
        topResults,
    },
    mounted() {
        $mediator.on(`search-${this.id}.clear`, () => {
            this.clearResults();
        });
    },
    methods: {
        getComponent(key) {
            if (key === 'clinical_trial') {
                return 'clinical-trial';
            }

            if (key === 'top_results') {
                return 'top-results';
            }

            return key;
        },
        toggleTab(tab) {
            if (tabbable !== null) {
                tabbable.destroy();
            }

            if (tab.data.length) {
                Object.keys(this.results).forEach(item => this.results[item].active = false);

                tab.active = true;
                this.state.currentTab = tab;

                this.$nextTick(() => {
                    tabbable = new Tabbable({
                        el: this.$refs.searchResults,
                        callback: () => {
                            if (this.$refs.activeTab.length) {
                                this.$refs.activeTab[0].focus();
                            }
                        }
                    });
                });
            }
        },
        clearResults() {
            this.results = $extend(true, {}, baseResults);
            this.resultsCount = 0;
        },
    },
    watch: {
        items() {
            this.clearResults();
            this.state.loading = true;

            this.items.map(({ data }) => {
                let obj = {};

                for (let key in data) {
                    if (data[key]) {
                        obj[key] = data[key].raw;

                        if (obj[key] === 'false' || obj[key] === 'true') {
                            obj[key] = obj[key] === 'true';
                        }
                    }
                }

                return obj;
            }).filter(item => item).forEach(item => {
                // Sort into correct category
                if (this.results[item.type]) {
                    this.results[item.type].data.push(item);
                } else {
                    this.results.other.data.push(item);
                }

                // Also add all results to top results
                this.results.top_results.data.push(item);
            });

            for (let type in this.results) {
                if (transforms[type] && $isFunction(transforms[type])) {
                    this.results[type].data = this.results[type].data.map(item => transforms[type](item));
                }

                if (type != 'top_results') {
                    this.resultsCount += this.results[type].data.length;
                }
            }

            this.state.loading = false;

            clearTimeout(tabbableTimeout);
            tabbable = null;

            this.results.top_results.active = true;

            // Debounce this, it's kind of heavy
            tabbableTimeout = setTimeout(() => {
                tabbable = new Tabbable({
                    el: this.$refs.searchResults,
                    callback() {
                        tabbable.$firstEl[0].focus();
                    }
                });
            }, 200);
        },
        suggestedTopResults() {
            this.filteredTopResults = this.suggestedTopResults.map(({ data }) => {
                let obj = {};

                for (let key in data) {
                    if (data[key]) {
                        obj[key] = data[key].raw;

                        if (obj[key] === 'false' || obj[key] === 'true') {
                            obj[key] = obj[key] === 'true';
                        }

                        if (obj[key] === 'clinical_trial') {
                            obj[key] = 'Clinical Trial';
                        }
                    }
                }

                return obj;
            })
        },
    },
    data: () => ({
        resultsCount: 0,
        results: $extend(true, {}, baseResults),
        filteredTopResults: [],
        state: {
            currentTab: {},
            currentTabIndex: -1,
            loading: true
        },
    })
}
</script>
